import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Users,
  Gear,
  ChartLine,
  CurrencyDollar,
  CheckCircle,
  Plus,
  Pencil,
  Trash,
  PaperPlaneTilt,
  Phone,
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { useAdminData } from '../hooks/useDashboardData';
import { StatCard, StatusBadge, ActiveBadge, LoadingSpinner, DashboardHeader } from '../components/DashboardComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

import { API_URL } from '../config/api';

// Chart axis config - extracted to avoid inline recreation
const chartAxisConfig = { fontSize: 12 };
const chartGridConfig = { strokeDasharray: '3 3', stroke: '#e2e8f0' };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const { analytics, clients, smsConfig, ussdConfig, otpSessions, loading, fetchData } = useAdminData();

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <DashboardHeader
        title="MojoMoney OTP Gateway"
        subtitle="Admin Dashboard"
        userName={user?.name}
        onLogout={logout}
        icon={PaperPlaneTilt}
      />

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1">
            <TabsTrigger value="overview" data-testid="tab-overview" className="data-[state=active]:bg-[#0B2545] data-[state=active]:text-white">
              <ChartLine size={18} className="mr-2" /> Overview
            </TabsTrigger>
            <TabsTrigger value="clients" data-testid="tab-clients" className="data-[state=active]:bg-[#0B2545] data-[state=active]:text-white">
              <Users size={18} className="mr-2" /> Clients
            </TabsTrigger>
            <TabsTrigger value="transactions" data-testid="tab-transactions" className="data-[state=active]:bg-[#0B2545] data-[state=active]:text-white">
              <Phone size={18} className="mr-2" /> OTP Sessions
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="data-[state=active]:bg-[#0B2545] data-[state=active]:text-white">
              <Gear size={18} className="mr-2" /> Gateways
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <OverviewStats analytics={analytics} />
            <OverviewCharts analytics={analytics} />
          </TabsContent>

          <TabsContent value="clients" className="space-y-6">
            <ClientsManager clients={clients} onRefresh={fetchData} />
          </TabsContent>

          <TabsContent value="transactions" className="space-y-6">
            <OTPSessionsTable sessions={otpSessions} onRefresh={fetchData} />
          </TabsContent>

          <TabsContent value="settings" className="space-y-6">
            <SMSConfigManager config={smsConfig} onRefresh={fetchData} />
            <USSDConfigManager config={ussdConfig} onRefresh={fetchData} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// Overview Stats Component
function OverviewStats({ analytics }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <StatCard
        title="Total OTP Requests"
        value={analytics?.summary?.total_otp || 0}
        icon={PaperPlaneTilt}
        color="blue"
      />
      <StatCard
        title="Verified"
        value={analytics?.summary?.total_verified || 0}
        icon={CheckCircle}
        trend="up"
        trendValue={`${analytics?.summary?.success_rate || 0}%`}
        color="green"
      />
      <StatCard
        title="Active Clients"
        value={analytics?.summary?.total_clients || 0}
        icon={Users}
        color="blue"
      />
      <StatCard
        title="Total Revenue"
        value={`R${(analytics?.summary?.total_revenue || 0).toFixed(2)}`}
        icon={CurrencyDollar}
        color="green"
      />
    </div>
  );
}

// Overview Charts Component
function OverviewCharts({ analytics }) {
  const dailyData = useMemo(() => analytics?.daily_stats || [], [analytics]);
  const statusData = useMemo(() => 
    Object.entries(analytics?.status_breakdown || {}).map(([k, v]) => ({ status: k, count: v })),
    [analytics]
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="dashboard-card">
        <CardHeader>
          <CardTitle className="text-lg">OTP Activity (Last 30 Days)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyData}>
                <CartesianGrid {...chartGridConfig} />
                <XAxis dataKey="_id" tick={chartAxisConfig} stroke="#94a3b8" />
                <YAxis tick={chartAxisConfig} stroke="#94a3b8" />
                <Tooltip />
                <Area type="monotone" dataKey="total" stroke="#0B2545" fill="#0B2545" fillOpacity={0.1} name="Total" />
                <Area type="monotone" dataKey="verified" stroke="#10B981" fill="#10B981" fillOpacity={0.2} name="Verified" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="dashboard-card">
        <CardHeader>
          <CardTitle className="text-lg">Status Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusData}>
                <CartesianGrid {...chartGridConfig} />
                <XAxis dataKey="status" tick={chartAxisConfig} stroke="#94a3b8" />
                <YAxis tick={chartAxisConfig} stroke="#94a3b8" />
                <Tooltip />
                <Bar dataKey="count" fill="#0B2545" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// OTP Sessions Table Component
function OTPSessionsTable({ sessions, onRefresh }) {
  return (
    <Card className="dashboard-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">OTP Sessions</CardTitle>
          <Button variant="outline" size="sm" onClick={onRefresh}>
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="table-overline">Session ID</TableHead>
              <TableHead className="table-overline">Phone</TableHead>
              <TableHead className="table-overline">Type</TableHead>
              <TableHead className="table-overline">Reference</TableHead>
              <TableHead className="table-overline">Status</TableHead>
              <TableHead className="table-overline">Cost</TableHead>
              <TableHead className="table-overline">Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sessions.map((session) => (
              <TableRow key={session.session_id}>
                <TableCell className="font-mono text-xs">{session.session_id?.slice(0, 8)}...</TableCell>
                <TableCell className="font-mono text-sm">{session.phone_number}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="uppercase text-xs">
                    {session.otp_type}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm">{session.transaction_reference}</TableCell>
                <TableCell><StatusBadge status={session.status} /></TableCell>
                <TableCell className="font-mono text-sm">R{session.cost?.toFixed(2)}</TableCell>
                <TableCell className="text-xs text-slate-500">
                  {new Date(session.created_at).toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function getBillingTierBadgeClass(tier) {
  if (tier === 'enterprise') return 'border-amber-300 text-amber-700 bg-amber-50';
  if (tier === 'pro') return 'border-blue-300 text-blue-700 bg-blue-50';
  return 'border-slate-300 text-slate-700';
}

// Clients Manager Component
function ClientsManager({ clients, onRefresh }) {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company_name: '',
    cost_per_sms: 0.10,
    cost_per_ussd: 0.05,
    billing_tier: 'basic',
    is_active: true,
  });

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      company_name: '',
      cost_per_sms: 0.10,
      cost_per_ussd: 0.05,
      billing_tier: 'basic',
      is_active: true,
    });
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/clients`, formData, { withCredentials: true });
      toast.success('Client created successfully');
      setIsCreateOpen(false);
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API_URL}/api/admin/clients/${editClient.id}`, {
        name: formData.name,
        company_name: formData.company_name,
        cost_per_sms: formData.cost_per_sms,
        cost_per_ussd: formData.cost_per_ussd,
        billing_tier: formData.billing_tier,
        is_active: formData.is_active,
      }, { withCredentials: true });
      toast.success('Client updated successfully');
      setEditClient(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update client');
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/clients/${clientId}`, { withCredentials: true });
      toast.success('Client deleted successfully');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    }
  };

  const openEditDialog = (client) => {
    setEditClient(client);
    setFormData({
      ...formData,
      name: client.name,
      company_name: client.company_name,
      cost_per_sms: client.cost_per_sms,
      cost_per_ussd: client.cost_per_ussd,
      billing_tier: client.billing_tier || 'basic',
      is_active: client.is_active,
    });
  };

  return (
    <Card className="dashboard-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Third-Party Clients</CardTitle>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0B2545] hover:bg-[#13315C]" data-testid="create-client-button">
                <Plus size={18} className="mr-2" /> Add Client
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Client</DialogTitle>
                <DialogDescription>Add a new third-party client to the system</DialogDescription>
              </DialogHeader>
              <ClientForm formData={formData} setFormData={setFormData} isCreate />
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                <Button onClick={handleCreate} className="bg-[#0B2545] hover:bg-[#13315C]" data-testid="submit-create-client">
                  Create Client
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="table-overline">Company</TableHead>
              <TableHead className="table-overline">Contact</TableHead>
              <TableHead className="table-overline">Email</TableHead>
              <TableHead className="table-overline">SMS Cost</TableHead>
              <TableHead className="table-overline">USSD Cost</TableHead>
              <TableHead className="table-overline">Tier</TableHead>
              <TableHead className="table-overline">Status</TableHead>
              <TableHead className="table-overline">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clients.map((client) => (
              <TableRow key={client.id}>
                <TableCell className="font-medium">{client.company_name}</TableCell>
                <TableCell>{client.name}</TableCell>
                <TableCell className="text-sm">{client.email}</TableCell>
                <TableCell className="font-mono">R{client.cost_per_sms?.toFixed(2)}</TableCell>
                <TableCell className="font-mono">R{client.cost_per_ussd?.toFixed(2)}</TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={`capitalize text-xs ${getBillingTierBadgeClass(client.billing_tier)}`}
                    data-testid={`client-tier-${client.id}`}
                  >
                    {client.billing_tier || 'basic'}
                  </Badge>
                </TableCell>
                <TableCell><ActiveBadge isActive={client.is_active} /></TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => openEditDialog(client)} data-testid={`edit-client-${client.id}`}>
                      <Pencil size={16} />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(client.id)} data-testid={`delete-client-${client.id}`}>
                      <Trash size={16} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={!!editClient} onOpenChange={() => setEditClient(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Client</DialogTitle>
          </DialogHeader>
          <ClientForm formData={formData} setFormData={setFormData} isCreate={false} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditClient(null)}>Cancel</Button>
            <Button onClick={handleUpdate} className="bg-[#0B2545] hover:bg-[#13315C]">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// Client Form Component
function ClientForm({ formData, setFormData, isCreate }) {
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-4 py-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Name</Label>
          <Input
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            data-testid="client-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Company</Label>
          <Input
            value={formData.company_name}
            onChange={(e) => handleChange('company_name', e.target.value)}
            data-testid="client-company-input"
          />
        </div>
      </div>
      {isCreate && (
        <>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              data-testid="client-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Password</Label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => handleChange('password', e.target.value)}
              data-testid="client-password-input"
            />
          </div>
        </>
      )}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Cost per SMS (R)</Label>
          <Input
            type="number"
            step="0.01"
            value={formData.cost_per_sms}
            onChange={(e) => handleChange('cost_per_sms', parseFloat(e.target.value))}
            data-testid="client-sms-cost-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Cost per USSD (R)</Label>
          <Input
            type="number"
            step="0.01"
            value={formData.cost_per_ussd}
            onChange={(e) => handleChange('cost_per_ussd', parseFloat(e.target.value))}
            data-testid="client-ussd-cost-input"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Billing Tier</Label>
        <Select value={formData.billing_tier || 'basic'} onValueChange={(value) => handleChange('billing_tier', value)}>
          <SelectTrigger data-testid="client-billing-tier-select">
            <SelectValue placeholder="Select tier" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="basic">Basic &mdash; 10/min, 100/hr, 500/day</SelectItem>
            <SelectItem value="pro">Pro &mdash; 30/min, 500/hr, 5,000/day (10% discount)</SelectItem>
            <SelectItem value="enterprise">Enterprise &mdash; 100/min, 2,000/hr, 50,000/day (25% discount)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {!isCreate && (
        <div className="flex items-center justify-between">
          <Label>Active Status</Label>
          <Switch
            checked={formData.is_active}
            onCheckedChange={(checked) => handleChange('is_active', checked)}
          />
        </div>
      )}
    </div>
  );
}

// SMS Config Manager Component
function SMSConfigManager({ config, onRefresh }) {
  const [formData, setFormData] = useState({
    sms_gateway_url: 'https://xml2sms.gsm.co.za/send/',
    sms_username: '',
    sms_password: '',
    callback_url: '',
    ems_enabled: false,
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (config) {
      setFormData({
        sms_gateway_url: config.sms_gateway_url || 'https://xml2sms.gsm.co.za/send/',
        sms_username: config.sms_username || '',
        sms_password: config.sms_password || '',
        callback_url: config.callback_url || '',
        ems_enabled: config.ems_enabled || false,
      });
    }
  }, [config]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/sms-config`, formData, { withCredentials: true });
      toast.success('SMS configuration saved');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="dashboard-card">
        <CardHeader>
          <CardTitle className="text-lg">XML2SMS Gateway Configuration</CardTitle>
          <CardDescription>Configure your Vodafone XML2SMS gateway credentials</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Gateway URL</Label>
            <Input
              value={formData.sms_gateway_url}
              onChange={(e) => handleChange('sms_gateway_url', e.target.value)}
              placeholder="https://xml2sms.gsm.co.za/send/"
              data-testid="sms-gateway-url-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Username</Label>
            <Input
              value={formData.sms_username}
              onChange={(e) => handleChange('sms_username', e.target.value)}
              placeholder="Your gateway username"
              data-testid="sms-username-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Password</Label>
            <Input
              type="password"
              value={formData.sms_password}
              onChange={(e) => handleChange('sms_password', e.target.value)}
              placeholder="Your gateway password"
              data-testid="sms-password-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Callback URL (for delivery reports)</Label>
            <Input
              value={formData.callback_url}
              onChange={(e) => handleChange('callback_url', e.target.value)}
              placeholder="https://your-domain.com/api/sms/delivery-callback"
              data-testid="sms-callback-url-input"
            />
          </div>
          <div className="flex items-center justify-between pt-2">
            <div>
              <Label>Enable EMS (Extended Messaging)</Label>
              <p className="text-xs text-slate-500">For messages longer than 160 characters</p>
            </div>
            <Switch
              checked={formData.ems_enabled}
              onCheckedChange={(checked) => handleChange('ems_enabled', checked)}
              data-testid="ems-toggle"
            />
          </div>
          <Button 
            onClick={handleSave} 
            disabled={isSaving}
            className="w-full bg-[#0B2545] hover:bg-[#13315C]"
            data-testid="save-sms-config-button"
          >
            {isSaving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </CardContent>
      </Card>

      <Card className="dashboard-card">
        <CardHeader>
          <CardTitle className="text-lg">Integration Guide</CardTitle>
          <CardDescription>Quick reference for XML2SMS API</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Base URL</h4>
            <code className="code-block block">https://xml2sms.gsm.co.za/send/</code>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Parameters</h4>
            <ul className="text-sm text-slate-600 space-y-1">
              <li><code className="mono bg-slate-100 px-1 rounded">username</code> - Account username</li>
              <li><code className="mono bg-slate-100 px-1 rounded">password</code> - Account password</li>
              <li><code className="mono bg-slate-100 px-1 rounded">number</code> - Recipient phone (E.164)</li>
              <li><code className="mono bg-slate-100 px-1 rounded">message</code> - Message content</li>
              <li><code className="mono bg-slate-100 px-1 rounded">ems</code> - Set to 1 for long messages</li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Response Codes</h4>
            <ul className="text-sm text-slate-600 space-y-1">
              <li><code className="mono bg-emerald-100 text-emerald-800 px-1 rounded">result="1"</code> - Success</li>
              <li><code className="mono bg-red-100 text-red-800 px-1 rounded">result="0"</code> - Failed</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// USSD Config Manager Component
function USSDConfigManager({ config, onRefresh }) {
  const [formData, setFormData] = useState({
    ussd_push_url: '',
    ussd_shortcode: '',
    ussd_username: '',
    ussd_password: '',
    ussd_callback_base_url: '',
    ussd_response_format: 'json',
    ussd_enabled: false,
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (config) {
      setFormData({
        ussd_push_url: config.ussd_push_url || '',
        ussd_shortcode: config.ussd_shortcode || '',
        ussd_username: config.ussd_username || '',
        ussd_password: config.ussd_password || '',
        ussd_callback_base_url: config.ussd_callback_base_url || '',
        ussd_response_format: config.ussd_response_format || 'json',
        ussd_enabled: config.ussd_enabled || false,
      });
    }
  }, [config]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/ussd-config`, formData, { withCredentials: true });
      toast.success('USSD configuration saved');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save USSD configuration');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="dashboard-card">
        <CardHeader>
          <CardTitle className="text-lg">Always Active Mobile USSD Gateway</CardTitle>
          <CardDescription>Configure USSD push notifications for transaction approval</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between pb-2 border-b border-slate-200">
            <div>
              <Label>Enable USSD Gateway</Label>
              <p className="text-xs text-slate-500">Activate USSD push for transaction approvals</p>
            </div>
            <Switch
              checked={formData.ussd_enabled}
              onCheckedChange={(checked) => handleChange('ussd_enabled', checked)}
              data-testid="ussd-enabled-toggle"
            />
          </div>
          <div className="space-y-2">
            <Label>Push API URL</Label>
            <Input
              value={formData.ussd_push_url}
              onChange={(e) => handleChange('ussd_push_url', e.target.value)}
              placeholder="https://ussd.alwaysactivemobile.com/push"
              data-testid="ussd-push-url-input"
            />
          </div>
          <div className="space-y-2">
            <Label>USSD Shortcode</Label>
            <Input
              value={formData.ussd_shortcode}
              onChange={(e) => handleChange('ussd_shortcode', e.target.value)}
              placeholder="*123#"
              data-testid="ussd-shortcode-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Username</Label>
              <Input
                value={formData.ussd_username}
                onChange={(e) => handleChange('ussd_username', e.target.value)}
                placeholder="API username"
                data-testid="ussd-username-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="password"
                value={formData.ussd_password}
                onChange={(e) => handleChange('ussd_password', e.target.value)}
                placeholder="API password"
                data-testid="ussd-password-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Callback Base URL</Label>
            <Input
              value={formData.ussd_callback_base_url}
              onChange={(e) => handleChange('ussd_callback_base_url', e.target.value)}
              placeholder="https://your-domain.com"
              data-testid="ussd-callback-base-url-input"
            />
            <p className="text-xs text-slate-500">
              The base URL where Always Active Mobile daemon will send callbacks
            </p>
          </div>
          <div className="space-y-2">
            <Label>Response Format</Label>
            <Select value={formData.ussd_response_format} onValueChange={(value) => handleChange('ussd_response_format', value)}>
              <SelectTrigger data-testid="ussd-response-format-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="json">JSON</SelectItem>
                <SelectItem value="xml">XML</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="w-full bg-[#0B2545] hover:bg-[#13315C]"
            data-testid="save-ussd-config-button"
          >
            {isSaving ? 'Saving...' : 'Save USSD Configuration'}
          </Button>
        </CardContent>
      </Card>

      <Card className="dashboard-card">
        <CardHeader>
          <CardTitle className="text-lg">USSD Integration Guide</CardTitle>
          <CardDescription>Always Active Mobile callback flow</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-2">How It Works</h4>
            <ol className="text-sm text-slate-600 space-y-2 list-decimal list-inside">
              <li>Third-party client generates OTP with <code className="mono bg-slate-100 px-1 rounded">otp_type: "ussd"</code></li>
              <li>System sends USSD push to user's phone via Always Active Mobile</li>
              <li>User sees transaction details with Approve/Reject options</li>
              <li>User responds, daemon calls our callback endpoint</li>
              <li>Session status updated, third-party polls for result</li>
            </ol>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Callback Endpoints</h4>
            <ul className="text-sm text-slate-600 space-y-2">
              <li>
                <code className="mono bg-blue-50 text-blue-800 px-1 rounded">GET /api/ussd/callback/entry</code>
                <p className="text-xs text-slate-500 mt-0.5">Initial menu with transaction details</p>
              </li>
              <li>
                <code className="mono bg-blue-50 text-blue-800 px-1 rounded">GET /api/ussd/callback/respond</code>
                <p className="text-xs text-slate-500 mt-0.5">Processes approve/reject response</p>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Query Parameters (from daemon)</h4>
            <ul className="text-sm text-slate-600 space-y-1">
              <li><code className="mono bg-slate-100 px-1 rounded">msisdn</code> - User's phone (intl. format)</li>
              <li><code className="mono bg-slate-100 px-1 rounded">provider</code> - Network (Vodacom, MTN, etc.)</li>
              <li><code className="mono bg-slate-100 px-1 rounded">sid</code> - OTP session ID</li>
              <li><code className="mono bg-slate-100 px-1 rounded">action</code> - approve or reject</li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Network Timeouts</h4>
            <ul className="text-sm text-slate-600 space-y-1">
              <li>Vodacom: <code className="mono bg-slate-100 px-1 rounded">3 min</code> session</li>
              <li>MTN: <code className="mono bg-slate-100 px-1 rounded">4 min</code> session</li>
              <li>CellC: <code className="mono bg-slate-100 px-1 rounded">3 min</code> session</li>
              <li>Telkom: <code className="mono bg-slate-100 px-1 rounded">3 min</code> session</li>
              <li>Application response: <code className="mono bg-amber-100 text-amber-800 px-1 rounded">10 sec max</code></li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
